import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Calendar, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const empty = { title: "", date: "", time: "", location: "", address: "", ustadz: "", description: "" };

export default function ManasikAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/manasik");
      setItems(data || []);
    } catch { toast.error("Gagal memuat"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); };
  const openEdit = (m) => {
    setEditing(m.id);
    setForm({ ...empty, ...m });
  };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.location) { toast.error("Judul, tanggal, lokasi wajib diisi"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/manasik", form);
        toast.success("Manasik ditambahkan");
      } else {
        await api.patch(`/admin/manasik/${editing}`, form);
        toast.success("Manasik diperbarui");
      }
      close();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus jadwal manasik ini?")) return;
    try { await api.delete(`/admin/manasik/${id}`); toast.success("Terhapus"); load(); }
    catch { toast.error("Gagal hapus"); }
  };

  return (
    <div data-testid="admin-manasik-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl text-primary flex items-center gap-2">
            <Calendar className="h-6 w-6 text-accent" /> Kelola Manasik
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Jadwal kajian & manasik yang tampil di beranda.</p>
        </div>
        <Button onClick={openNew} data-testid="manasik-add" className="inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Tambah Manasik
        </Button>
      </div>

      <div className="border border-border rounded-lg bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-primary">
            <tr>
              <th className="text-left p-3">Judul</th>
              <th className="text-left p-3">Tanggal</th>
              <th className="text-left p-3">Lokasi</th>
              <th className="text-left p-3">Ustadz</th>
              <th className="text-right p-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Belum ada jadwal manasik.</td></tr>}
            {!loading && items.map((m) => (
              <tr key={m.id} className="border-t border-border" data-testid={`manasik-row-${m.id}`}>
                <td className="p-3 text-primary font-medium">{m.title}</td>
                <td className="p-3 text-muted-foreground">{m.date}{m.time ? ` ${m.time}` : ""}</td>
                <td className="p-3 text-muted-foreground">{m.location}</td>
                <td className="p-3 text-muted-foreground">{m.ustadz || "-"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => openEdit(m)} className="text-primary hover:text-accent mr-3" data-testid={`manasik-edit-${m.id}`}>
                    <Edit className="inline h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => del(m.id)} className="text-red-600 hover:text-red-800" data-testid={`manasik-delete-${m.id}`}>
                    <Trash2 className="inline h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="manasik-modal">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 relative">
            <button onClick={close} className="absolute top-3 right-3 text-muted-foreground hover:text-primary" data-testid="manasik-close">
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-serif text-2xl text-primary">{editing === "new" ? "Tambah Manasik" : "Edit Manasik"}</h3>
            <form onSubmit={save} className="mt-5 space-y-3">
              <Input required placeholder="Judul manasik / kajian" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mn-title" />
              <div className="grid grid-cols-2 gap-3">
                <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mn-date" />
                <Input placeholder="09:00" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="mn-time" />
              </div>
              <Input required placeholder="Lokasi (nama tempat)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="mn-loc" />
              <Input placeholder="Alamat lengkap" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="mn-addr" />
              <Input placeholder="Ustadz pembicara" value={form.ustadz} onChange={(e) => setForm({ ...form, ustadz: e.target.value })} data-testid="mn-ust" />
              <Textarea placeholder="Deskripsi singkat…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="mn-desc" />
              <Button type="submit" disabled={saving} className="w-full" data-testid="mn-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
